import React, { useCallback, useEffect, useState } from "react";
import EventDetailsCard from "../components/event-details-card";
import EventDetailsModal from "../components/event-details-modal";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DateRangePicker } from "react-dates";
import moment from "moment";
import {
  FaSearch,
  FaTh,
  FaList,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaPhoneAlt,
  FaCopy,
  FaLockOpen,
} from "react-icons/fa";
import handleCopy from "../helpers/copyToClipboard";
import { SuccessToast } from "../components/toastify";
import { useDispatch } from "react-redux";
import { setActivePage, setShowPreloader } from "../store/activePage";
import HandleGetEventList from "../hooks/handleGetEventList";
import handleExportReport from "../hooks/handleExportReport";
import Swal from "sweetalert2";
import getEventCreds from "../hooks/getEventCreds";
import CustomModal from "../components/modal";
import handleRequestReopen from "../hooks/handleRequestReopen";

const listBtnColors = {
  blue: "bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200",
  emerald:
    "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200",
  orange: "bg-orange-50 text-orange-600 hover:bg-orange-100 border-orange-200",
};

function ListActionBtn({ color = "blue", title, onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`w-7 h-7 rounded-lg border flex items-center justify-center text-xs transition-colors ${listBtnColors[color]}`}
    >
      {children}
    </button>
  );
}

function ServicePill({ active, label }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
        Number(active) > 0
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-gray-100 text-gray-400 border-gray-200"
      }`}
    >
      {Number(active) > 0 ? "✓" : "✗"} {label}
    </span>
  );
}

// ─── Credential Card ─────────────────────────────────────────────────────────
function CredentialCard({ items, onCopyAll }) {
  const initials = (items.operator_name || "OP")
    .split(" ")
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const copyField = async (label, value) => {
    const ok = await handleCopy(value);
    if (ok) SuccessToast(`${label} copied`);
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-xl shadow-slate-200/60 mb-3 text-left">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white font-black text-sm shrink-0 select-none">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm leading-tight truncate">
            {items.operator_name}
          </p>
          <p className="text-orange-100/90 text-xs mt-0.5">{items.operator_mobile}</p>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 bg-orange-700/40 text-orange-100 border border-orange-400/50 rounded-full uppercase tracking-widest shrink-0">
          Operator
        </span>
      </div>

      {/* Credential fields */}
      <div className="bg-gray-50 px-5 py-4 space-y-3">
        {[
          { label: "Username", value: items.username },
          { label: "Password", value: items.password },
        ].map(({ label, value }) => (
          <div key={label}>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
              {label}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono font-semibold text-gray-900 bg-white border border-gray-200 rounded-lg px-3 py-2 min-w-0 truncate">
                {value}
              </code>
              <button
                onClick={() => copyField(label, value)}
                className="shrink-0 w-8 h-8 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-orange-600 transition"
                title={`Copy ${label}`}
              >
                <FaCopy size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer actions */}
      <div className="bg-white border-t border-gray-200 px-5 py-3 flex justify-end">
        <button
          onClick={onCopyAll}
          className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition"
        >
          <FaCopy size={11} /> Copy All Credentials
        </button>
      </div>
    </div>
  );
}

const OperatorAllClosedEventList = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);

  const [eventDetails, setEventDetails] = useState([]);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [eventCreds, setEventCreds] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };
  const handlePreset = (type) => {
    const yesterday = moment().subtract(1, "days"); // Current date with moment
    let newStartDate = yesterday.clone(); // Clone yesterday
    let newEndDate = yesterday.clone(); // Clone yesterday

    switch (type) {
      case "Before 1 Week":
        newStartDate = yesterday.clone().subtract(1, "weeks"); // Add 6 days for 1 week
        break;
      case "Before 1 Month":
        newStartDate = yesterday.clone().subtract(1, "months"); // Add 1 month
        break;
      case "Before 3 Months":
        newStartDate = yesterday.clone().subtract(3, "months"); // Add 3 months
        break;
      default:
        newStartDate = null;
        newEndDate = null;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Closed Events",
        activeMenu: "eventsHistory",
      })
    );
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue((searchValue || "").trim());
      setPage(1);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);

  const HandleShowEventCreds = async (id) => {
    const creds = await getEventCreds(id);
    if (creds) {
      setEventCreds(creds);
    }
  };

  const getEventList = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const eventList = await HandleGetEventList(
      startDate?.format("YYYY-MM-DD") || null,
      endDate?.format("YYYY-MM-DD") || null,
      debouncedSearchValue,
      "closed",
      false,
      { page, perPage: 10 },
    );
    dispatch(setShowPreloader(false));
    if (eventList && Array.isArray(eventList.items)) {
      setEventDetails(eventList.items);
      setPagination(
        eventList.pagination || {
          page,
          per_page: 10,
          total: 0,
          total_pages: 0,
        },
      );
    } else {
      setEventDetails([]);
      setPagination({
        page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
      });
    }
  }, [startDate, endDate, debouncedSearchValue, dispatch, page]);

  useEffect(() => {
    getEventList();
  }, [getEventList]);

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-200/60 backdrop-blur">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-teal-700">
            Event Filters
          </p>
          <p className="text-xs text-gray-500">
            Search and date range controls
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1 shadow-sm">
            <button
              className={`rounded-md p-2 transition ${
                viewMode === "grid"
                  ? "bg-teal-700 text-white shadow"
                  : "text-gray-600 hover:bg-teal-50"
              }`}
              onClick={() => setViewMode("grid")}
              title="Grid View"
            >
              <FaTh />
            </button>
            <button
              className={`rounded-md p-2 transition ${
                viewMode === "list"
                  ? "bg-teal-700 text-white shadow"
                  : "text-gray-600 hover:bg-teal-50"
              }`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <FaList />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 transition hover:bg-teal-100"
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-6 rounded-xl border border-white/70 bg-white/90 p-6 shadow-xl shadow-slate-200/60 backdrop-blur">
          <div className="w-full md:w-1/2">
            <p className="mb-3 text-lg font-semibold text-gray-900">
              Select Date
            </p>
            <DateRangePicker
              startDate={startDate}
              startDateId="closed_start_date"
              isOutsideRange={(day) =>
                day.isAfter(moment().subtract(1, "days"), "day")
              }
              endDate={endDate}
              endDateId="closed_end_date"
              onDatesChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
              }}
              focusedInput={focusedInput}
              onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
              displayFormat="MMM DD, YYYY"
            />

            <div className="mt-4 flex flex-wrap gap-3">
              {[
                { label: "Before 1 Week", color: "orange" },
                { label: "Before 1 Month", color: "orange" },
                { label: "Before 3 Months", color: "orange" },
              ].map(({ label }) => (
                <button
                  key={label}
                  className="rounded-lg bg-orange-50 px-4 py-2 text-sm font-medium text-orange-600 transition hover:bg-orange-100"
                  onClick={() => handlePreset(label)}
                >
                  {label}
                </button>
              ))}
              <button
                className="rounded-lg bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-100"
                onClick={() => handlePreset("Reset")}
              >
                Reset
              </button>
            </div>
          </div>

          <div className="flex w-full items-center gap-4 md:w-1/2">
            <div className="flex flex-1 items-center overflow-hidden rounded-lg border border-gray-200 shadow-sm">
              <input
                type="text"
                placeholder="Search here..."
                className="h-11 w-full px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
                onInput={(event) => {
                  setSearchValue(event.target.value);
                }}
              />
              <button className="h-11 bg-gray-50 px-4 transition hover:bg-gray-100">
                <FaSearch className="text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      )}

      {eventDetails?.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {eventDetails?.map((event, index) => (
              <EventDetailsCard
                key={index}
                eventDetails={event}
                onClick={(details) => {
                  setSelectedEvent(details);
                }}
                cardStyle={"bg-orange-200 text-center"}
                onDownloadReport={(event_id) => {
                  Swal.fire({
                    title: "Do you want to download this report?",
                    showCancelButton: true,
                    confirmButtonText: "Download Report",
                    confirmButtonColor: "green",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      handleExportReport(event_id, dispatch);
                    }
                  });
                }}
                onRequestReopen={(event_id) => {
                  Swal.fire({
                    title: "Do you want to request re-opening this event?",
                    showCancelButton: true,
                    confirmButtonText: "Yes, Request",
                    confirmButtonColor: "#f97316",
                  }).then(async (result) => {
                    if (result.isConfirmed) {
                      const success = await handleRequestReopen(event_id, dispatch);
                      if (success) getEventList();
                    }
                  });
                }}
                tokenCount={event.token_count}
                total_reminder_list={event.total_reminder_list}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {eventDetails.map((event) => {
              const noOperator =
                event?.operators?.[0]?.operator_name === null ||
                !event?.operators?.[0]?.operator_name;
              const location =
                [event.event_address, event.event_city, event.event_state]
                  .filter(Boolean)
                  .join(", ") || "—";

              return (
                <div
                  key={event.event_id}
                  className="bg-white/90 rounded-xl border border-white/70 shadow-xl shadow-slate-200/60 hover:shadow-lg transition-shadow overflow-hidden backdrop-blur"
                >
                  <div className="flex">
                    <div className="w-1.5 shrink-0 bg-teal-500" />

                    <div className="flex-1 px-4 py-3 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <button
                            className="text-sm font-bold text-gray-900 hover:text-blue-600 transition text-left truncate max-w-xs"
                            onClick={() => {
                              setSelectedEvent(event);
                            }}
                          >
                            {event.event_name || event.event_type || "—"}
                          </button>
                          <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full border border-slate-200 tracking-wide shrink-0">
                            Event ID #{event.event_id}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full border border-gray-300 uppercase tracking-wide shrink-0">
                            Closed
                          </span>
                          {noOperator && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full border border-orange-200 uppercase tracking-wide shrink-0">
                              No Operator
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1 shrink-0 flex-wrap">
                          <ListActionBtn
                            color="orange"
                            title="Download Report"
                            onClick={() =>
                              Swal.fire({
                                title: "Do you want to download this report?",
                                showCancelButton: true,
                                confirmButtonText: "Download Report",
                                confirmButtonColor: "green",
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  handleExportReport(event.event_id, dispatch);
                                }
                              })
                            }
                          >
                            <FaDownload />
                          </ListActionBtn>
                          
                          {event.reopen_requested === 1 || event.reopen_requested === "1" ? (
                            <span className="text-[10px] font-semibold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded border border-orange-200">
                              Re-open Requested
                            </span>
                          ) : (
                            <ListActionBtn
                              title="Request Re-open"
                              color="orange"
                              onClick={() => {
                                Swal.fire({
                                  title: "Do you want to request re-opening this event?",
                                  showCancelButton: true,
                                  confirmButtonText: "Yes, Request",
                                  confirmButtonColor: "#f97316",
                                }).then(async (result) => {
                                  if (result.isConfirmed) {
                                    const success = await handleRequestReopen(
                                      event.event_id,
                                      dispatch
                                    );
                                    if (success) getEventList();
                                  }
                                });
                              }}
                            >
                              <FaLockOpen />
                            </ListActionBtn>
                          )}
                        </div>
                      </div>

                      <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2">
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <span className="mt-0.5 text-gray-400 shrink-0 text-[10px] font-black uppercase tracking-wide">
                            Event
                          </span>
                          <span className="font-semibold leading-snug text-gray-800 truncate">
                            {event.event_name || event.event_type || "—"}
                          </span>
                        </div>

                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaPhoneAlt className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug truncate">
                            {event.customer_name
                              ? `${event.customer_name}${event.customer_mobile ? ` · ${event.customer_mobile}` : ""}`
                              : event.customer_mobile || "—"}
                          </span>
                        </div>

                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaUser className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug truncate">
                            {[event.eventor_1, event.eventor_2].filter(Boolean).join(", ") || "--"}
                          </span>
                        </div>

                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaCalendarAlt className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug">
                            {event.event_date || "—"}
                          </span>
                        </div>

                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaMapMarkerAlt className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug truncate">
                            {location}
                          </span>
                        </div>

                        <div className="flex items-start gap-1.5 text-xs">
                          <FaUser className="mt-0.5 text-gray-400 shrink-0" />
                          <span
                            className={`font-medium leading-snug ${
                              noOperator ? "text-red-500" : "text-gray-600"
                            }`}
                          >
                            {event?.operators?.[0]?.operator_name ||
                              "Not assigned"}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 flex-wrap">
                          <ServicePill
                            active={event.reminder_sms}
                            label="SMS"
                          />
                          <ServicePill
                            active={event.reminder_voice}
                            label="Call"
                          />
                          <ServicePill
                            active={event.moi_confirmation_sms}
                            label="Confirm"
                          />
                          {event.token_count && (
                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">
                              {event.token_count} tokens
                            </span>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <p className="text-center text-xl font-semibold text-teal-600">
          No Events to Show
        </p>
      )}

      {pagination.total > 0 && (
        <div className="mt-6 flex flex-col gap-3 rounded-xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-200/60 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold text-slate-900">
              Page {pagination.page} of {Math.max(pagination.total_pages || 1, 1)}
            </p>
            <p className="text-xs text-slate-500">
              Showing {Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)}-
              {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} events
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FaChevronLeft />
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                setPage((current) =>
                  Math.min(current + 1, pagination.total_pages || current + 1),
                )
              }
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Show modal if an event is selected */}
      {selectedEvent && (
        <EventDetailsModal
          eventDetails={selectedEvent}
          onClose={handleCloseModal}
          isEditable={false} // Pass whether the modal should be editable or not
        />
      )}
      <CustomModal
        showModal={eventCreds.length > 0}
        type="Event Credentials"
        bodycontent={
          <div className="mt-3 space-y-1">
            {eventCreds.map((items) => (
              <CredentialCard
                key={items.operator_id}
                items={items}
                onCopyAll={async () => {
                  const copied = await handleCopy(
                    `Username: ${items.username}\nPassword: ${items.password}\nResponsible person: ${items.operator_name}-${items.operator_mobile}\n\nYou are solely responsible for your credentials. Please do not share them with anyone.\nநீங்கள் உங்கள் உள்நுழைவு விவரங்களுக்கு முழுப் பொறுப்பும் வகிக்கின்றீர்கள். தயவுசெய்து இதை யாருடனும் பகிர வேண்டாம்.`,
                  );
                  if (copied)
                    SuccessToast(
                      "Credentials copied. Please share to the respective person",
                    );
                }}
              />
            ))}
          </div>
        }
        closeModal={() => setEventCreds([])}
      />
    </div>
  );
};
export default OperatorAllClosedEventList;
